import { ColumnDef } from '@tanstack/react-table';
import { formatUtils } from '@/lib/utils';
import { t } from 'i18next';
import { Hash, UserRoundCheck } from 'lucide-react';

import {
  DataTableFilter,
  RowDataWithActions,
} from '@/components/ui/data-table';
import { DataTableColumnHeader } from '@/components/ui/data-table/data-table-column-header';
import { AdminFlow } from '@activepieces/shared';

import {
  ActiveStatusBadge,
  CellWithCopyTooltip,
  PieceIconsCell,
} from './admin-table-cells';

export const useAdminFlowsTable = () => {
  const columns: ColumnDef<RowDataWithActions<AdminFlow>>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Name')} />
      ),
      cell: ({ row }) => (
        <CellWithCopyTooltip
          displayValue={row.original.name ?? 'N/A'}
          tooltipValue={row.original.id}
        />
      ),
    },
    {
      accessorKey: 'pieceNames',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Apps')} />
      ),
      cell: ({ row }) => (
        <PieceIconsCell pieceNames={row.original.pieceNames} maxIcons={2} />
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Status')} />
      ),
      cell: ({ row }) => (
        <ActiveStatusBadge isActive={row.original.status === 'ENABLED'} />
      ),
    },
    {
      accessorKey: 'externalId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('User ID')} />
      ),
      cell: ({ row }) => row.original.externalId ?? 'N/A',
    },
    {
      accessorKey: 'externalCustomerId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Client ID')} />
      ),
      cell: ({ row }) => row.original.externalCustomerId ?? 'N/A',
    },
    {
      accessorKey: 'created',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Created')} />
      ),
      cell: ({ row }) => formatUtils.formatDate(new Date(row.original.created)),
    },
    {
      accessorKey: 'updated',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Updated')} />
      ),
      cell: ({ row }) => formatUtils.formatDate(new Date(row.original.updated)),
    },
  ];

  const filters: DataTableFilter<keyof AdminFlow>[] = [
    {
      type: 'input',
      title: t('Search'),
      accessorKey: 'name',
      options: [],
    },
    {
      type: 'input',
      title: t('User ID'),
      accessorKey: 'externalId',
      icon: Hash,
      options: [],
    },
    {
      type: 'input',
      title: t('Client ID'),
      accessorKey: 'externalCustomerId',
      icon: Hash,
      options: [],
    },
    {
      type: 'select',
      title: t('Status'),
      accessorKey: 'status',
      icon: UserRoundCheck,
      options: [
        { label: t('Active'), value: 'ENABLED' },
        { label: t('Inactive'), value: 'DISABLED' },
      ],
    },
  ];

  return { columns, filters };
};
